import React, { useState, useContext, useEffect, useMemo } from "react"
import clsx from "clsx"

import {
    AppBar,
    Toolbar,
    Typography,
    MenuItem,
    IconButton,
    Menu,
    Avatar,
    Box,
} from "@mui/material"

import { makeStyles, useTheme } from "@mui/styles"

import MenuIcon from "@mui/icons-material/Menu"

import NotificationsPopOver from "../components/NotificationsPopOver"
import UserModal from "../components/UserModal"
import { AuthContext } from "../context/Auth/AuthContext"
import BackdropLoading from "../components/BackdropLoading"
import { i18n } from "../translate/i18n"
import toastError from "../errors/toastError"
import AnnouncementsPopover from "../components/AnnouncementsPopover"

import ChatPopover from "../pages/Chat/ChatPopover"

// DARK THEMA
import ColorModeContext from "../layout/themeContext"
import { SocketContext } from "../context/Socket/SocketContext"
import Sidebar from "../components/SideMenu"
import { useWindowSize } from "usehooks-ts"
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material"
import LeadsPopover from "../components/LeadsPopover"
import ConfirmationModal from "../components/ConfirmationModal"

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        height: "100vh",
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 56px)",
        },
        backgroundColor: theme.palette.fancyBackground,
    },

    toolbar: {
        paddingRight: 24,
        color: theme.palette.topBarTextColor,
        background: theme.palette.barraSuperior.gradient
            ? `linear-gradient(to right, ${theme.palette.barraSuperior.gradient[0]}, ${theme.palette.barraSuperior.gradient[1]})`
            : theme.palette.barraSuperior,
        display: "flex",
        justifyContent: "space-between",
    },

    appBar: {
        zIndex: 0,
        transition: "width 0.3s ease",
    },
    menuButton: {
        marginRight: 36,
    },
    menuButtonHidden: {
        display: "none",
    },
    title: {
        flexGrow: 1,
        fontSize: 14,
    },

    appBarSpacer: {
        minHeight: "61px",
    },
    content: {
        flex: 1,
        overflow: "auto",
        ...theme.scrollbarStyles,
    },
}))

const LoggedInLayout = ({ children }) => {
    const { width } = useWindowSize()
    const classes = useStyles()
    const [userModalOpen, setUserModalOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const { handleLogout, loading } = useContext(AuthContext)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const { user } = useContext(AuthContext)
    const { socket } = useContext(SocketContext)

    const [modalPlan, setModalPlan] = useState(false)

    const theme = useTheme()
    const { colorMode } = useContext(ColorModeContext)

    const collapsewidth = useMemo(() => (width <= 640 ? 0 : 80), [width])

    const drawerWidth = useMemo(
        () => (drawerOpen ? 300 : collapsewidth),
        [drawerOpen],
    )

    useEffect(() => {
        if (document.body.offsetWidth > 640) {
            setDrawerOpen(true)
        }
    }, [])

    useEffect(() => {
        const companyId = localStorage.getItem("companyId")
        const userId = localStorage.getItem("userId")

        if (socket) {
            socket.on(`company-${companyId}-auth`, (data) => {
                if (data.user.id === +userId) {
                    toastError("Sua conta foi acessada em outro computador.")
                    setTimeout(() => {
                        localStorage.clear()
                        window.location.reload()
                    }, 1000)
                }
            })

            socket.on(`company-${companyId}-plan`, () => {
                localStorage.clear()
                setModalPlan(true)
            })

            socket.emit("userStatus")
            setInterval(
                () => {
                    socket.emit("userStatus")
                },
                1000 * 60 * 5,
            )
        }
    }, [socket])

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget)
        setMenuOpen(true)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
        setMenuOpen(false)
    }

    const handleOpenUserModal = () => {
        setUserModalOpen(true)
        handleCloseMenu()
    }

    const handleClickLogout = () => {
        handleCloseMenu()
        handleLogout()
    }

    const toggleColorMode = () => {
        colorMode.toggleColorMode()
    }

    if (loading) {
        return <BackdropLoading />
    }

    return (
        <div className={classes.root}>
            <Sidebar drawerOpen={drawerOpen} />
            <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                userId={user?.id}
            />
            <ConfirmationModal
                open={modalPlan}
                title="Atualização de plano"
                onConfirm={() => window.location.reload()}
            >
                Sua assinatura foi atualizada, obrigado! Você deve fazer login
                novamente, para que as alterações tenham efeito.
            </ConfirmationModal>
            <AppBar
                position="absolute"
                className={classes.appBar}
                sx={{
                    marginLeft: drawerWidth,
                    width: `calc(100% - ${drawerWidth}px) !important`,
                }}
                color="primary"
                elevation={0}
            >
                <Toolbar variant="regular" className={classes.toolbar}>
                    <IconButton
                        edge="start"
                        variant="contained"
                        aria-label="open drawer"
                        onClick={() => setDrawerOpen(!drawerOpen)}
                        className={clsx(
                            classes.menuButton,
                            drawerOpen && classes.menuButtonHidden,
                        )}
                    >
                        <MenuIcon
                            style={{
                                color: theme.palette.topBarIconsColor,
                            }}
                        />
                    </IconButton>

                    <Typography
                        color="inherit"
                        noWrap
                        className={classes.title}
                    >
                        Olá {user.name}
                    </Typography>

                    <Box display="flex" alignItems="center">
                        <IconButton edge="start" onClick={toggleColorMode}>
                            {theme.palette.mode === "dark" ? (
                                <LightModeOutlined
                                    style={{
                                        color: theme.palette.topBarIconsColor,
                                    }}
                                />
                            ) : (
                                <DarkModeOutlined
                                    style={{
                                        color: theme.palette.topBarIconsColor,
                                    }}
                                />
                            )}
                        </IconButton>

                        <LeadsPopover />
                        {user.id && <NotificationsPopOver />}

                        <AnnouncementsPopover />
                        <ChatPopover />

                        <div>
                            <IconButton
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                variant="contained"
                                style={{
                                    color: theme.palette.topBarIconsColor,
                                }}
                            >
                                <Avatar sx={{ width: 33, height: 33 }} />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                }}
                                open={menuOpen}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={handleOpenUserModal}>
                                    {i18n.t("mainDrawer.appBar.user.profile")}
                                </MenuItem>
                                <MenuItem onClick={handleClickLogout}>
                                    {i18n.t("mainDrawer.appBar.user.logout")}
                                </MenuItem>
                            </Menu>
                        </div>
                    </Box>
                </Toolbar>
            </AppBar>
            <main className={classes.content}>
                <div className={classes.appBarSpacer} />

                {children ? children : null}
            </main>
        </div>
    )
}

export default LoggedInLayout
